import type { FC, ReactNode } from 'react';
import { StyleSheet } from 'react-native';
import { Chip } from 'react-native-paper';

import { DownIcon } from '@/assets';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props = {
  /** Avatar or organization logo rendered on the leading side. */
  avatar: ReactNode;
  /** Display name of the current sender (person or organization). */
  label: string;
  /** Disables both the chip and its trailing chevron. */
  disabled?: boolean;
  /** Opens the sender selection (also wired to the trailing chevron). */
  onPress: () => void;
};

/**
 * Presentational sender chip: leading avatar/logo + name + chevron, rendered
 * centered (e.g. in place of the navigation bar title). Holds no data logic —
 * the caller decides what avatar and label to pass.
 */
export const SenderChip: FC<Props> = ({ avatar, label, disabled, onPress }) => {
  const theme = useAppTheme();

  return (
    <Chip
      testID="post-creation-sender-chip"
      mode="flat"
      compact
      elevated
      accessibilityRole="button"
      disabled={disabled}
      avatar={avatar}
      closeIcon={
        disabled
          ? undefined
          : ({ size }) => (
              <DownIcon
                width={size}
                height={size}
                fill={theme.colors.primary}
              />
            )
      }
      onClose={disabled ? undefined : onPress}
      onPress={onPress}
      // Plain label string: the chip's own text styles/numberOfLines apply —
      // a nested <Text> would be ignored for line clamping on iOS.
      textStyle={disabled ? undefined : { color: theme.colors.primary }}
      style={[
        styles.chip,
        disabled
          ? undefined
          : {
              backgroundColor: 'white',
              borderColor: theme.colors.outlineVariant
            }
      ]}
    >
      {label}
    </Chip>
  );
};

const styles = StyleSheet.create({
  chip: {
    alignSelf: 'center',
    borderWidth: 1,
    borderRadius: 999,
    maxWidth: '100%'
  }
});
